import * as qs from 'qs';

import { APIError, type Environment } from './models/common.js';

const servers: { [key in Environment]: string } = {
  dev: 'http://localhost:6363',
  staging: 'https://app.notablehealth.com',
  demo: 'https://demo.vivaa.engineering',
  prod: 'https://api.vivaa.engineering',
  webbucket: 'https://app.notablehealth.com',

  // web apps are served from API server either directly (prod) or via proxy (dev)
  weblocal: '/api',
  webprod: '',
};

export class Common {
  env: Environment;
  server: string;
  sid: string | null;
  bearerToken: string | null;

  constructor(env: Environment) {
    this.env = env;
    this.server = env ? servers[env] : servers.prod;
    this.sid = null;
    this.bearerToken = null;
  }

  setEnv(env: Environment) {
    this.env = env;
    this.server = servers[env] || servers.prod;
  }

  setSid(sid: string) {
    this.sid = sid;
  }

  setBearerToken(bearerToken: string) {
    this.bearerToken = bearerToken;
  }

  constructPath(path: string, query?: object): string {
    if (!path.startsWith('/')) {
      path = '/' + path;
    }
    if (query) {
      return `${this.server}${path}?${qs.stringify(query)}`;
    }
    return `${this.server}${path}`;
  }

  constructWsPath(path: string) {
    // local dev case because http calls are proxy'ed but ws calls are not
    const isLocalDev = window.location.hostname === 'localhost';
    return `${(isLocalDev
      ? window.location.origin.replace('3004', '6363')
      : window.location.origin
    ).replace(/^(http|https)?/, 'ws')}${path}`;
  }

  authedFetch(url: string, init?: RequestInit): Promise<Response> {
    // Cannot be undefined for old Edge versions
    const headers = new Headers(init?.headers ?? {});
    if (this.sid) {
      headers.append('cookie', `connect.sid=${this.sid}`);
    }
    if (this.bearerToken) {
      headers.append('Authorization', `Bearer ${this.bearerToken}`);
    }
    return fetch(url, {
      ...init,
      credentials: init?.credentials ?? 'same-origin',
      headers,
    });
  }

  async getJSON(url: string, init?: RequestInit, returnErrorStatus: boolean = false) {
    const response = await this.authedFetch(url, init);
    if (response.status === 204) return {};
    return await this.handleResponse(response, returnErrorStatus);
  }

  async getBlob(url: string, init?: RequestInit): Promise<{ status: number; dataURL: string }> {
    const response = await this.authedFetch(url, init);
    const { status } = response;
    const blob = await response.blob();
    return { status, dataURL: URL.createObjectURL(blob) };
  }

  async postFormData(
    url: string,
    formData: FormData,
    method = 'POST',
    init?: RequestInit,
    returnErrorStatus: boolean = false
  ) {
    const response = await this.authedFetch(url, {
      ...init,
      method,
      headers: {
        Accept: 'application/json',
      },
      body: formData,
    });
    return await this.handleResponse(response, returnErrorStatus);
  }

  async postJSON(
    url: string,
    body: Record<string, any> | undefined,
    method = 'POST',
    init?: RequestInit,
    returnErrorStatus: boolean = false
  ) {
    const response = await this.authedFetch(url, {
      ...init,
      method,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    return await this.handleResponse(response, returnErrorStatus);
  }

  async putJSON(url: string, body?: Record<string, any>) {
    return await this.postJSON(url, body, 'PUT');
  }

  async patchJSON(url: string, body?: Record<string, any>) {
    return await this.postJSON(url, body, 'PATCH');
  }

  async deleteJSON(url: string, init?: RequestInit) {
    const response = await this.authedFetch(url, {
      ...init,
      method: 'DELETE',
      headers: {
        Accept: 'application/json',
      },
    });

    if (response.status === 204) {
      console.log('success with 204 status code');
      return;
    }

    return await this.handleResponse(response, false);
  }

  private async handleResponse(response: Response, returnErrorStatus: boolean) {
    if (!response.ok && returnErrorStatus) {
      return await Promise.reject({
        status: response.status,
        message: await response.text(),
      });
    }
    const text = await response.text();
    if (response.status >= 400) {
      let data;
      try {
        data = text; // in case JSON.parse() fails
        data = JSON.parse(text);
      } catch (err) {
        // ignore
      }
      console.log(
        `api.common.handleResponse : got error status of ${response.status}, throwing error`,
        data
      );
      throw new APIError(data, response.status, response.statusText);
    }
    return text ? JSON.parse(text) : null;
  }
}

export class BaseAPI {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }
}
